import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Stack, IconButton, Chip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import { Plan, PlanRole, PlanArtifact } from '../../types/generated/graphql';
import { buildTaskTree, PlanProgress } from '../../utils/taskTree';
import { colors } from '../../theme/colors';
import OverviewPlaybookSpace from './OverviewPlaybookSpace';
import { TasksTwoPaneLayout } from './tasks-pane';
import TimelineV2View from './TimelineV2View';
import OutputsPane from './OutputsPane';
import StepDetailDialog from '../StepDetailDialog';
import PlanSummaryCard from '../PlanSummaryCard';
import RoleAssignmentDialog from '../RoleAssignmentDialog';

export type TabValue = 'overview' | 'tasks' | 'when' | 'outputs';

interface TabPanelProps {
  children?: React.ReactNode;
  value: TabValue;
  currentValue: TabValue;
}

function TabPanel({ children, value, currentValue }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== currentValue}
      id={`plan-tabpanel-${value}`}
      aria-labelledby={`plan-tab-${value}`}
    >
      {value === currentValue && children}
    </Box>
  );
}

export type FilterValue = 'all' | 'mine' | 'blocked' | 'completed';

export interface PlanTabContainerProps {
  plan: Plan;
  currentTab: TabValue;
  selectedStepId: string | null;
  planProgress: PlanProgress;
  onToggleStepCompletion: (stepId: string, completed: boolean) => void;
  onUpdateStepContext: (
    stepId: string,
    context: { notes?: string | null; links?: string[] }
  ) => Promise<void>;
  onStepSelect: (stepId: string | null) => void;
}

export const PlanTabContainer: React.FC<PlanTabContainerProps> = ({
  plan,
  currentTab,
  selectedStepId,
  planProgress,
  onToggleStepCompletion,
  onStepSelect,
}) => {
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [dialogStepId, setDialogStepId] = useState<string | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<PlanRole | null>(null);

  // Get the step for the dialog (overview/timeline clicks)
  const dialogStep = dialogStepId
    ? plan.steps.find((s) => s.id === dialogStepId) || null
    : null;

  // Build the task tree from flat steps
  const taskTree = useMemo(() => buildTaskTree(plan.steps), [plan.steps]);

  // Navigate to a specific task - opens dialog (for overview/timeline tabs)
  const navigateToTask = useCallback(
    (taskId: string) => {
      setDialogStepId(taskId);
      setStepDialogOpen(true);
    },
    []
  );

  // Close step dialog
  const handleCloseStepDialog = useCallback(() => {
    setStepDialogOpen(false);
    setDialogStepId(null);
  }, []);

  // Handle role assignment request
  const handleAssignRole = useCallback(
    (roleId: string) => {
      const role = (plan.roles || []).find((r) => r.roleId === roleId);
      if (role) {
        setSelectedRole(role);
        setRoleDialogOpen(true);
      }
    },
    [plan.roles]
  );

  // Close role assignment dialog
  const handleCloseRoleDialog = useCallback(() => {
    setRoleDialogOpen(false);
    setSelectedRole(null);
  }, []);

  // Handle step completion toggle from dialog
  const handleDialogToggleCompletion = useCallback(
    (stepId: string, completed: boolean) => {
      onToggleStepCompletion(stepId, completed);
    },
    [onToggleStepCompletion]
  );

  // Get phases sorted by order
  const sortedPhases = useMemo(
    () => [...plan.phases].sort((a, b) => a.order - b.order),
    [plan.phases]
  );

  return (
    <Box>
      {/* Tab panels */}
      <TabPanel value="overview" currentValue={currentTab}>
        {/* Plan summary card for Overview tab */}
        <PlanSummaryCard plan={plan} planProgress={planProgress} />

        {/* Attached files from plan creation */}
        {plan.artifacts && plan.artifacts.length > 0 && (
          <ArtifactsSection artifacts={plan.artifacts} />
        )}

        <OverviewPlaybookSpace
          planId={plan.id}
          taskTree={taskTree}
          steps={plan.steps}
          participants={plan.participants || []}
          roles={plan.roles || []}
          onNavigateToTask={navigateToTask}
          onAssignRole={handleAssignRole}
        />
      </TabPanel>

      <TabPanel value="tasks" currentValue={currentTab}>
        <TasksTwoPaneLayout
          planId={plan.id}
          steps={plan.steps}
          phases={sortedPhases}
          participants={plan.participants || []}
          roles={plan.roles || []}
          selectedStepId={selectedStepId}
          onStepSelect={onStepSelect}
          onToggleStepCompletion={onToggleStepCompletion}
        />
      </TabPanel>

      <TabPanel value="when" currentValue={currentTab}>
        <TimelineV2View
          steps={plan.steps}
          participants={plan.participants || []}
          roles={plan.roles || []}
          onNavigateToTask={navigateToTask}
          facts={plan.facts as Record<string, unknown> | null}
        />
      </TabPanel>

      <TabPanel value="outputs" currentValue={currentTab}>
        <OutputsPane
          plan={plan}
          phases={sortedPhases}
          onNavigateToStep={navigateToTask}
        />
      </TabPanel>

      {/* Step Detail Dialog (for overview/timeline tab clicks) */}
      <StepDetailDialog
        open={stepDialogOpen}
        step={dialogStep}
        planId={plan.id}
        onClose={handleCloseStepDialog}
        onToggleCompletion={handleDialogToggleCompletion}
      />

      {/* Role Assignment Dialog */}
      <RoleAssignmentDialog
        open={roleDialogOpen}
        onClose={handleCloseRoleDialog}
        planId={plan.id}
        role={selectedRole}
        participants={plan.participants || []}
      />
    </Box>
  );
};

// ============================================================================
// Artifacts Section - displays files attached during plan creation
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <ImageIcon sx={{ color: colors.blue[500] }} />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon sx={{ color: '#E53935' }} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv')
    return <TableChartIcon sx={{ color: '#43A047' }} />;
  return <InsertDriveFileIcon sx={{ color: colors.gray[500] }} />;
}

const ArtifactsSection: React.FC<{ artifacts: PlanArtifact[] }> = ({ artifacts }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: colors.gray[50],
          borderBottom: `1px solid ${colors.gray[200]}`,
        }}
      >
        <AttachFileIcon sx={{ fontSize: 18, color: colors.gray[500] }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
          Attached Files
        </Typography>
        <Chip
          label={artifacts.length}
          size="small"
          sx={{
            ml: 'auto',
            bgcolor: 'white',
            height: 22,
            '& .MuiChip-label': { px: 1, fontSize: '0.75rem', color: colors.textSecondary },
          }}
        />
      </Box>
      <Stack divider={<Box sx={{ borderBottom: `1px solid ${colors.gray[100]}` }} />}>
        {artifacts.map((artifact) => (
          <Box
            key={artifact.id}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            {getFileIcon(artifact.mimeType)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: colors.textPrimary,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {artifact.fileName}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                {formatFileSize(artifact.sizeBytes)} &middot; {artifact.category}
              </Typography>
            </Box>
            {artifact.downloadUrl && (
              <IconButton
                size="small"
                href={artifact.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                sx={{ color: colors.gray[400], '&:hover': { color: colors.blue[600] } }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default PlanTabContainer;

